<template>
    <div v-if="homepageContent" class="flex space-x-2">
        <button @click="preOpenModal" :class="!homepageContent && 'border border-slate-600 rounded-xl px-3.5 py-3 transition hover:bg-gray-200'">
            <QrIcon :icon="homepageContent ? 'pen' : 'file'" class="w-5"/>
        </button>

        <DeleteHomePageContent v-if="homepageContent" class="ml-auto" :content="homepageContent"/>
    </div>
    <div v-else class="group relative flex gap-x-6 rounded-lg p-4 hover:bg-gray-50">
        <div class="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
            <QrIcon icon="file" class="h-6 w-6 text-gray-600 group-hover:text-brand" aria-hidden="true" />
        </div>
        <div>
            <button @click="preOpenModal" class="font-semibold text-gray-900">
                {{ trans('homepage.document') }}
                <span class="absolute inset-0" />
            </button>
            <p class="mt-1 text-gray-600" v-text="trans('homepage.document_desc')"/>
        </div>
    </div>

    <QrModal v-model="modalOpen" :title="trans('homepage.document')">
        <QrForm @submit.prevent="update" class="space-y-4">
            <QrInput v-model="menuButtonName" :label="trans('homepage.menu_button_name')" trans="homepage.menu_button_name"/>

            <div v-if="homepageContent && homepageContent.file" class="flex">
                <div class="mx-auto relative">
                    <a
                        target="_blank" :href="homepageContent.file.file"
                        class="absolute top-0 right-0 bg-slate-600 text-white px-1.5 py-0.5 rounded-lg hover:bg-black"
                    >
                        <QrIcon icon="download"/>
                    </a>

                    <Thumbnail
                        v-if="homepageContent"
                        :path="homepageContent.file.file"
                        :classes="['rounded-md object-cover w-36']"
                    />
                </div>
            </div>

            <QrFilesUpload
                v-if="uploadFilesFunc"
                :upload-files="uploadFilesFunc"
                :helper-text="trans('form.document-types-and-size')"
            />

            <QrToggle v-model="passwordProtected" :label="trans('homepage.protect-with-password')"/>
            <QrInput
                v-if="!qrCode.homepage.is_password_protected && passwordProtected"
                v-model="password"
                :errors="validationErrors.password"
                :label="trans('homepage.password')"
                trans="homepage.password"
            />
        </QrForm>

        <template #buttons>
            <QrButton
                :text="trans('button.save')"
                trans="button.save"
                @click="update"
                :loading="loading"
                :recently-succeeded="recentlySucceeded"
                :recently-failed="recentlyFailed"
            />
        </template>
    </QrModal>
</template>

<script setup>
import {onMounted, ref, watch} from "vue";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import QrModal from "@/components/application-ui/dialogs/QrModal.vue";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import QrInput from "@/components/application-ui/form/QrInput.vue";
import { trans } from "@/libs/helper.js";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import { useQrCodeHomepageStore } from "@/store/qrCodeHomepage.js";
import QrToggle from "@/components/application-ui/form/QrToggle.vue";
import QrFilesUpload from "@/components/application-ui/form/QrFilesUpload.vue";
import createUploader from "@/libs/file-uploader.js";
import { useQrCodesStore } from "@/store/qrCodes.js";
import { storeToRefs } from "pinia";
import { useStatesStore } from "@/store/states.js";
import DeleteHomePageContent from "@/components/qr-code/homepage/DeleteHomePageContent.vue";
import emitter from "@/libs/emitter.js";
import Thumbnail from "@/components/image/Thumbnail.vue";

const emit = defineEmits(['close']);

const props = defineProps({
    homepageContent: Object,
});

const homepageStore = useQrCodeHomepageStore();
const qrCodeStore = useQrCodesStore();
const statesStore = useStatesStore();

const { qrCode } = storeToRefs(qrCodeStore);
const modalOpen = ref(false);
const menuButtonName = ref('');
const passwordProtected = ref(false);
const password = ref('');
const uploadFilesFunc = ref(null);
const content = ref({
    autoplay: false,
    loop: false,
});

const {
    loading,
    validationErrors,
    recentlyFailed,
    recentlySucceeded
} = storeToRefs(statesStore);

emitter.on('created-document', (id) => {
    if (id === props.homepageContent.id) {
        preOpenModal();
    }
});

const uploadComplete = ({ status, data }) => {
    if (status === 200) {
        qrCode.value = data.data
        modalOpen.value = false;
    }
};

const preOpenModal = async () => {
    menuButtonName.value = props.homepageContent.menu_button_name;
    passwordProtected.value = props.homepageContent.is_password_protected;
    content.value = props.homepageContent.content;
    modalOpen.value = true;
};

const update = async () => {
    await homepageStore.updateContent(props.homepageContent.id, {
        menu_button_name: menuButtonName.value,
        content: content.value,
        is_password_protected: passwordProtected.value,
        password: password.value,
    });

    if (Object.keys(validationErrors.value).length === 0) {
        modalOpen.value = false
    }
}

onMounted(() => {
    const { uploadFiles } = createUploader(
        `/api/qr-codes/${qrCode.value.id}/homepage/${props.homepageContent.id}/file`,
        uploadComplete
    );

    uploadFilesFunc.value = uploadFiles
});

watch(modalOpen, () => {
    if (! modalOpen.value) {
        emit('close');
    }
});
</script>
